import { useContext, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronUp, Disc3, Home, ListMusic, MicVocal, Music2, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { UserContext } from '../../../context/UserContext';
import { AvatarMenu } from './AvatarMenu';
import type { SettingsTab } from './AvatarMenu';
import { Avatar } from './Avatar';
import { SettingsModal } from '../settings/SettingsModal';
import musiceerMark from '../../../assets/musiceer-mark.png';
import './Rail.css';

const NAV_ITEMS = [
    { path: '/home', label: 'Home', icon: Home },
    { path: '/albums', label: 'Albums', icon: Disc3 },
    { path: '/artists', label: 'Artists', icon: MicVocal },
    { path: '/songs', label: 'Songs', icon: Music2 },
    { path: '/playlists', label: 'Playlists', icon: ListMusic },
];

export function Rail(): React.JSX.Element {
    const [open, setOpen] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);
    const avatarRef = useRef<HTMLButtonElement>(null);
    const { user, logout } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Display name = first_name (the "Display name" field in Account
    // settings), falling back to the username when unset. Role label is
    // only shown for admins - an empty string renders nothing.
    const displayName = user?.firstName?.trim() || user?.username || '';
    const roleLabel = user?.isAdmin ? 'Admin' : '';

    return (
        <nav className={`rail ${open ? 'railOpen' : 'railClosed'}`}>
            <div className="railTop">
                <div className="railLogo">
                    <img className="railLogoMark" src={musiceerMark} alt="" />
                    {open && <span className="railLogoText">Musiceer</span>}
                </div>
                <button
                    className="railToggle"
                    onClick={() => setOpen((o) => !o)}
                    aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
                >
                    {open ? (
                        <PanelLeftClose size={20} strokeWidth={2.75} />
                    ) : (
                        <PanelLeftOpen size={20} strokeWidth={2.75} />
                    )}
                </button>
            </div>

            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
                <div
                    key={path}
                    className={`railNavItem ${location.pathname.startsWith(path) ? 'railNavItemActive' : ''}`}
                    onClick={() => navigate(path)}
                >
                    <Icon size={18} strokeWidth={2.75} />
                    <span className="railNavItemLabel">{label}</span>
                </div>
            ))}

            <div className="railBottom">
                <button
                    className={`railAvatarTrigger ${menuOpen ? 'railAvatarTriggerOpen' : ''}`}
                    ref={avatarRef}
                    onClick={() => setMenuOpen((o) => !o)}
                >
                    <Avatar userId={user?.id} avatarVersion={user?.avatarVersion} className="railAvatarCircle" />
                    {open && user && (
                        <>
                            <span className="railAvatarIdentity">
                                <span className="railAvatarName">{displayName}</span>
                                {roleLabel && <span className="railAvatarRole">{roleLabel}</span>}
                            </span>
                            <ChevronUp size={16} strokeWidth={2.75} className="railAvatarChevron" />
                        </>
                    )}
                </button>
            </div>

            {menuOpen && avatarRef.current && (
                <AvatarMenu
                    anchorRect={avatarRef.current.getBoundingClientRect()}
                    userId={user?.id}
                    avatarVersion={user?.avatarVersion}
                    userName={displayName}
                    userHandle={`@${user?.username ?? ''}${roleLabel ? ` · ${roleLabel}` : ''}`}
                    onSelectTab={(tab) => setSettingsTab(tab)}
                    onLogout={handleLogout}
                    onClose={() => setMenuOpen(false)}
                />
            )}

            {settingsTab && (
                <SettingsModal
                    activeTab={settingsTab}
                    onChangeTab={setSettingsTab}
                    onClose={() => setSettingsTab(null)}
                />
            )}
        </nav>
    );
}
